// ═══ Studio Walkthrough — Guided Tour with driver.js ═══
// Triggered on first visit OR as a hand-off from the Cinematic Demo (task-016).
// When the origin is the cinematic, skip the generic welcome — the cinematic
// already said "Listen." and "Your Turn." A fresh "Welcome" popover would undo
// that invitation. Start directly at Play/Pause.
//
// Core UX shape (v2 polish, 2026-04-17):
// - Copy anchors to moments, not buttons ("press space and the canvas breathes"
//   is stronger than "click this to play").
// - The final step anchors to the canvas itself — an invitation, not a summary.
// - driver.js popovers are styled to match the Studio palette (amber glow,
//   warm white text, deep backdrop).

import { driver, type DriveStep, type Config } from 'driver.js'
import 'driver.js/dist/driver.css'

const WALKTHROUGH_KEY = 'studio-walkthrough-seen'

export interface WalkthroughOptions {
  /** true when triggered by the Cinematic Demo hand-off at t=33s.
   * Skips the welcome title card — the cinematic already framed the moment. */
  fromCinematic?: boolean
}

// ═══ Step library ═══
// Each step is intentionally short. The room does the explaining; the popover
// just names what's happening.

const welcomeStep: DriveStep = {
  popover: {
    title: 'The Studio',
    description:
      'A canvas for building lyric visualizers. The tool disappears. What stays is you and the canvas.',
    side: 'bottom',
    align: 'center',
  },
}

const playStep: DriveStep = {
  element: '[data-tour="play-btn"]',
  popover: {
    title: 'Play',
    description:
      'Space, or this button. The canvas reacts to the audio — beats push the particles, lyrics move with the groove.',
    side: 'top',
    align: 'center',
  },
}

const scrubberStep: DriveStep = {
  element: '[data-tour="scrubber"]',
  popover: {
    title: 'Scrub',
    description:
      'Drag to any moment. Lyrics, effects, and notes follow the playhead.',
    side: 'top',
    align: 'center',
  },
}

const notesStep: DriveStep = {
  element: '[data-tour="notes-btn"]',
  popover: {
    title: 'Notes live on the painting',
    description:
      'Click anywhere on the canvas to leave a note — it attaches to that exact moment. For collaborators, for future-you.',
    side: 'top',
    align: 'center',
  },
}

const timelineStep: DriveStep = {
  element: '[data-tour="timeline-btn"]',
  popover: {
    title: 'Timeline (T)',
    description: 'Sections, notes, playhead — a strip at the bottom when you want it, gone when you don’t.',
    side: 'top',
    align: 'center',
  },
}

const editorStep: DriveStep = {
  element: '[data-tour="editor-btn"]',
  popover: {
    title: 'Editor',
    description:
      'Import audio (MP3/WAV), drop in lyrics (SRT), carve sections. This is where the piece gets built.',
    side: 'top',
    align: 'center',
  },
}

const exportStep: DriveStep = {
  element: '[data-tour="export-btn"]',
  popover: {
    title: 'Render',
    description:
      'Landscape, portrait, square, or HD — one format or all of them in a batch.',
    side: 'top',
    align: 'center',
  },
}

const settingsStep: DriveStep = {
  element: '[data-tour="settings-btn"]',
  popover: {
    title: 'Sharing',
    description:
      'Private, shared with named collaborators, or open to the community gallery. Your call per project.',
    side: 'top',
    align: 'center',
  },
}

// Final step — anchored to the canvas, not a center title card.
// Short. Invitational. The tour ends *on* the canvas, not above it.
const readyStep: DriveStep = {
  element: '[data-tour="canvas"]',
  popover: {
    title: 'Your canvas',
    description: 'Click anywhere to leave a note. Space to play. Open the Editor when you’re ready to build.',
    side: 'top',
    align: 'center',
  },
}

// ═══ Step assembly ═══

function buildSteps(opts: WalkthroughOptions): DriveStep[] {
  const steps: DriveStep[] = []
  // Skip welcome when the cinematic just handed off — don't undo "Your Turn."
  if (!opts.fromCinematic) steps.push(welcomeStep)
  steps.push(
    playStep,
    scrubberStep,
    notesStep,
    timelineStep,
    editorStep,
    exportStep,
    settingsStep,
    readyStep
  )
  return steps
}

// ═══ Popover styling ═══
// Injected once on first walkthrough trigger. Matches the Studio's amber /
// warm-white / deep-backdrop palette and coexists with the cinematic.

let stylesInjected = false
function injectStyles() {
  if (stylesInjected) return
  stylesInjected = true
  const css = `
    .studio-tour-popover.driver-popover {
      background: linear-gradient(180deg, #11121A 0%, #080A12 100%);
      border: 1px solid rgba(232, 166, 58, 0.18);
      color: #FFF5E8;
      font-family: Playfair Display, Georgia, serif;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 245, 232, 0.04);
      border-radius: 10px;
      max-width: 320px;
    }
    .studio-tour-popover .driver-popover-title {
      color: #FFF5E8;
      font-size: 20px;
      font-weight: 600;
      font-style: italic;
      letter-spacing: 0.01em;
      margin-bottom: 6px;
    }
    .studio-tour-popover .driver-popover-description {
      color: rgba(255, 245, 232, 0.82);
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
      font-style: normal;
    }
    .studio-tour-popover .driver-popover-navigation-btns button {
      background: rgba(232, 166, 58, 0.18);
      color: #FFF5E8;
      border: 1px solid rgba(232, 166, 58, 0.35);
      font-family: system-ui, sans-serif;
      font-size: 13px;
      padding: 6px 14px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s;
    }
    .studio-tour-popover .driver-popover-navigation-btns button:hover {
      background: rgba(232, 166, 58, 0.3);
    }
    .studio-tour-popover .driver-popover-close-btn {
      color: rgba(255, 245, 232, 0.5);
    }
    .studio-tour-popover .driver-popover-progress-text {
      color: rgba(255, 245, 232, 0.45);
      font-size: 11px;
    }
    .studio-tour-popover .driver-popover-arrow-side-top.driver-popover-arrow,
    .studio-tour-popover .driver-popover-arrow-side-bottom.driver-popover-arrow,
    .studio-tour-popover .driver-popover-arrow-side-left.driver-popover-arrow,
    .studio-tour-popover .driver-popover-arrow-side-right.driver-popover-arrow {
      border-color: #11121A;
    }
    /* Spotlight — warmer than default */
    .driver-overlay {
      backdrop-filter: blur(2px);
    }
  `
  const styleEl = document.createElement('style')
  styleEl.setAttribute('data-studio-tour-styles', '')
  styleEl.textContent = css
  document.head.appendChild(styleEl)
}

// ═══ Public API ═══

export function startWalkthrough(opts: WalkthroughOptions = {}) {
  injectStyles()
  const config: Config = {
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    steps: buildSteps(opts),
    popoverClass: 'studio-tour-popover',
    onDestroyed: () => {
      try {
        localStorage.setItem(WALKTHROUGH_KEY, 'true')
      } catch {
        // localStorage unavailable — fail silent
      }
    },
  }
  const d = driver(config)
  d.drive()
}

/** Start the walkthrough after a delay, for chaining off the cinematic demo. */
export function startWalkthroughDeferred(
  delayMs: number,
  opts: WalkthroughOptions = {}
) {
  window.setTimeout(() => startWalkthrough(opts), Math.max(0, delayMs))
}

export function shouldShowWalkthrough(): boolean {
  try {
    return !localStorage.getItem(WALKTHROUGH_KEY)
  } catch {
    return false
  }
}

export function resetWalkthrough() {
  try {
    localStorage.removeItem(WALKTHROUGH_KEY)
  } catch {
    // noop
  }
}
